import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/cn'
import { getLucideIcon } from '@/lib/lucideIcon'
import { type Theme, useUIStore } from '@/stores/ui.store'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { Command } from 'cmdk'
import {
  ArrowRight,
  Clock,
  LogOut,
  type LucideIcon,
  Moon,
  Rows2,
  Rows3,
  Search,
  Settings,
  Sun,
  UserCircle,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const PALETTE_EVENT = 'amanet:command-palette'
const RECENTS_KEY = 'amanet_recent_pages'
const MAX_RECENTS = 5

/** Fire from anywhere to open the palette (used by the header trigger). */
export function openCommandPalette() {
  window.dispatchEvent(new Event(PALETTE_EVENT))
}

type PageEntry = { path: string; label: string; icon: LucideIcon; group: string }
type Recent = { path: string; label: string }

function readRecents(): Recent[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY)
    return raw ? (JSON.parse(raw) as Recent[]) : []
  } catch {
    return []
  }
}

function pushRecent(entry: Recent) {
  try {
    const next = [entry, ...readRecents().filter((r) => r.path !== entry.path)].slice(
      0,
      MAX_RECENTS,
    )
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next))
  } catch {
    // localStorage unavailable — silent.
  }
}

export function CommandPalette() {
  const { t } = useTranslation(['common', 'workspaces'])
  const { available, switchTo, current } = useWorkspace()
  const navigate = useNavigate()
  const theme = useUIStore((s) => s.theme)
  const setTheme = useUIStore((s) => s.setTheme)
  const density = useUIStore((s) => s.density)
  const toggleDensity = useUIStore((s) => s.toggleDensity)
  const { logout } = useAuth()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const [open, setOpen] = useState(false)
  const [recents, setRecents] = useState<Recent[]>([])

  // Flat, de-duplicated index of every reachable module page.
  const pages = useMemo<PageEntry[]>(() => {
    const byPath = new Map<string, PageEntry>()
    for (const ws of available) {
      const group = t(`workspaces:workspaces.${ws.code}.name`, ws.name)
      for (const m of ws.primary_modules) {
        if (!m.path || byPath.has(m.path)) continue
        byPath.set(m.path, {
          path: m.path,
          label: m.label,
          icon: getLucideIcon(m.icon),
          group,
        })
      }
    }
    return [...byPath.values()]
  }, [available, t])

  // Record the current location into recents whenever it maps to a known page.
  useEffect(() => {
    const match = pages
      .filter((p) => pathname === p.path || pathname.startsWith(`${p.path}/`))
      .sort((a, b) => b.path.length - a.path.length)[0]
    if (match) pushRecent({ path: match.path, label: match.label })
  }, [pathname, pages])

  // Global ⌘K / Ctrl+K + custom event trigger.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    const onEvent = () => setOpen(true)
    window.addEventListener('keydown', onKey)
    window.addEventListener(PALETTE_EVENT, onEvent)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener(PALETTE_EVENT, onEvent)
    }
  }, [])

  // Refresh recents each time the palette opens.
  useEffect(() => {
    if (open) setRecents(readRecents())
  }, [open])

  const go = useCallback(
    (to: string) => {
      setOpen(false)
      void navigate({ to: to as never })
    },
    [navigate],
  )

  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches)

  const recentPages = recents
    .map((r) => pages.find((p) => p.path === r.path) ?? { ...r, icon: Clock, group: '' })
    .filter((p) => p.path !== pathname)
    .slice(0, MAX_RECENTS)

  const itemClass =
    'flex h-10 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm text-foreground transition-colors data-[selected=true]:bg-muted data-[selected=true]:text-foreground'

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label={t('common:command.title', 'Recherche & actions')}
      shouldFilter
      overlayClassName="fixed inset-0 z-[100] bg-ink-900/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in"
      contentClassName="fixed left-1/2 top-[15vh] z-[101] w-[92vw] max-w-xl -translate-x-1/2 overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-xl"
    >
      <div className="flex items-center gap-2 border-b border-border px-4">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <Command.Input
          autoFocus
          placeholder={t('common:command.placeholder', 'Rechercher une page ou une action…')}
          className="h-12 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
        />
      </div>

      <Command.List className="max-h-[60vh] overflow-y-auto p-2">
        <Command.Empty className="py-8 text-center text-sm text-muted-foreground">
          {t('common:command.empty', 'Aucun résultat.')}
        </Command.Empty>

        {recentPages.length > 0 ? (
          <Command.Group
            heading={t('common:command.recent', 'Récents')}
            className="px-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {recentPages.map((p) => {
              const Icon = p.icon
              return (
                <Command.Item
                  key={`recent-${p.path}`}
                  value={`recent ${p.label} ${p.path}`}
                  onSelect={() => go(p.path)}
                  className={itemClass}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{p.label}</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                </Command.Item>
              )
            })}
          </Command.Group>
        ) : null}

        <Command.Group
          heading={t('common:command.pages', 'Pages')}
          className="px-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
        >
          {pages.map((p) => {
            const Icon = p.icon
            return (
              <Command.Item
                key={p.path}
                value={`${p.group} ${p.label} ${p.path}`}
                onSelect={() => go(p.path)}
                className={itemClass}
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{p.label}</span>
                <span className="shrink-0 text-xs text-muted-foreground/70">{p.group}</span>
              </Command.Item>
            )
          })}
        </Command.Group>

        <Command.Group
          heading={t('common:command.actions', 'Actions')}
          className="px-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
        >
          <Command.Item
            value="action theme toggle dark light mode thème"
            onSelect={() => {
              const next: Theme = isDark ? 'light' : 'dark'
              setTheme(next)
              setOpen(false)
            }}
            className={itemClass}
          >
            {isDark ? (
              <Sun className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <Moon className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="flex-1">
              {isDark
                ? t('common:user_menu.light_mode', 'Mode clair')
                : t('common:user_menu.dark_mode', 'Mode sombre')}
            </span>
          </Command.Item>

          <Command.Item
            value="action density densité compact comfortable affichage"
            onSelect={() => {
              toggleDensity()
              setOpen(false)
            }}
            className={itemClass}
          >
            {density === 'compact' ? (
              <Rows3 className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <Rows2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="flex-1">
              {density === 'compact'
                ? t('common:command.density_comfortable', 'Affichage confortable')
                : t('common:command.density_compact', 'Affichage compact')}
            </span>
          </Command.Item>

          <Command.Item
            value="action profile profil settings paramètres"
            onSelect={() => go('/settings/profile')}
            className={itemClass}
          >
            <UserCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">{t('common:user_menu.profile', 'Profil')}</span>
          </Command.Item>

          <Command.Item
            value="action preferences préférences settings paramètres"
            onSelect={() => go('/settings/preferences')}
            className={itemClass}
          >
            <Settings className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">{t('common:user_menu.preferences', 'Préférences')}</span>
          </Command.Item>

          <Command.Item
            value="action logout déconnexion signout"
            onSelect={() => {
              setOpen(false)
              logout.mutate()
            }}
            className={cn(itemClass, 'text-muted-foreground')}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className="flex-1">{t('common:user_menu.signout', 'Déconnexion')}</span>
          </Command.Item>
        </Command.Group>

        {available.length > 1 ? (
          <Command.Group
            heading={t('common:command.workspaces', 'Espaces de travail')}
            className="px-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
          >
            {available.map((ws) => {
              const Icon = getLucideIcon(ws.icon)
              const name = t(`workspaces:workspaces.${ws.code}.name`, ws.name)
              return (
                <Command.Item
                  key={`ws-${ws.code}`}
                  value={`workspace espace ${name} ${ws.code}`}
                  onSelect={() => {
                    switchTo(ws.code)
                    setOpen(false)
                  }}
                  className={itemClass}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{name}</span>
                  {ws.code === current?.code ? (
                    <span className="shrink-0 text-xs text-primary">●</span>
                  ) : null}
                </Command.Item>
              )
            })}
          </Command.Group>
        ) : null}
      </Command.List>
    </Command.Dialog>
  )
}
